import { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../../../store/auth-context";
import { GlobalContext } from "../../../store/global-context";
import { ServiceRequestContext } from "../../../store/service-request";
import styles from './ServiceRequest.module.css';
import axios from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MdMiscellaneousServices } from "react-icons/md";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { IoAdd } from "react-icons/io5";
import { Link } from "react-router-dom";
import { InputTextarea } from 'primereact/inputtextarea';
import { IoMdDownload } from "react-icons/io";
import { FilterMatchMode } from 'primereact/api';
import { FloatLabel } from 'primereact/floatlabel';
import { InputText } from "primereact/inputtext";
import { errorMessage } from "../../Util/errorMessage";
import { useDialogIsolation } from "../../Util/useDialogIsolation";
import Select from "../../Util/Select";
import PageHeading from "../../Util/PageHeading";
import { ColumnFilterList, columnProps, tablePassThrough, useTableScrollRegion } from "../../Util/dataTable";
import { supportingDocumentError } from "../../Util/supportingDocument";
import SupportingDocumentUpload, { UPLOAD_ID } from "./SupportingDocumentUpload";
import { busyButtonProps } from "../../Util/busyButton";
import { useDialogSession, useFormMessages } from "../../Util/formSession";

// RCMC-18 (v1.3 ticket 06): the mandatory fields (owner ruling Q14), in form order. `key` is the form-state
// field, `id` the control's element id, `label` its visible name, `error` what it shows when left empty.
// The Supporting Document is optional.
const REQUIRED_FIELDS = [
  { key: "company_name", id: "sr-company", label: "Company", error: "Select a company." },
  { key: "folio_no", id: "folio-no", label: "Folio No / DP-Client ID", error: "Enter your Folio No or DP-Client ID." },
  { key: "certificate_no", id: "certificate-no", label: "Certificate No / PAN No", error: "Enter your Certificate No or PAN No." },
  { key: "request_type", id: "sr-request-type", label: "Request Type", error: "Select a request type." },
  { key: "description", id: "description", label: "Description", error: "Describe your request." },
];
const FIELD = Object.fromEntries(REQUIRED_FIELDS.map((field) => [field.key, field]));

// Empty or spaces only (a company id is a number, so compare as text)
const isBlank = (value) => String(value ?? "").trim() === "";

// The alert-region summary of a failed Add (owner ruling 12 Oct): how many, then which, by visible label
const missingSummary = (missing) =>
  `${missing.length} required field${missing.length === 1 ? " is" : "s are"} empty — ${missing.map((field) => field.label).join(", ")}.`;

// A blank form; certificate_no is listed so its field is controlled from the start
const EMPTY_REQUEST = {
  company_name:'',
  folio_no:'',
  certificate_no:'',
  request_type:'',
  description:'',
  supporting_document:''
};

const ServiceRequest = () => {
  const inputRef = useRef();
  const dt = useRef(null);
  // RCMC-04 (ticket 16): the table's region is a Tab stop while the table is too wide for it
  useTableScrollRegion(dt);
  const authCtx = useContext(AuthContext);
  const globalCtx = useContext(GlobalContext);
  const serviceCtx = useContext(ServiceRequestContext);

  // filters
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    created_on: { value: null, matchMode: FilterMatchMode.IN },
    request_id: { value: null, matchMode: FilterMatchMode.IN },
    status_display: { value: null, matchMode: FilterMatchMode.IN },
  });

  // Ticket 22 (Q35): true while an Add is on its way — the Add button stays focused and says it is busy
  const [isAddingNew, setAddingNew] = useState(false);
  const [isAddNewModalVisible, setIsAddNewModalVisible] = useState(false);
  // Ticket 22 (21's review findings, shared with Comments.jsx): a reply touches only the opening of the dialog that sent
  // it, and the form takes away only the messages it raised itself (Util/formSession.js)
  const dialogSession = useDialogSession();
  const messages = useFormMessages();
  // RCMC-23: page behind inert while open, focus to the first field; RCMC-02: named close button
  const raiseDialogIsolation = useDialogIsolation(isAddNewModalVisible, "Raise Service Request");
  const [selectedRowCount, setSelectedRowCount] = useState(10);
  const [selectedNewValues, setSelectedNewValues] = useState(EMPTY_REQUEST);
  // RCMC-18: error text per form-state key, set by a failed Add; a field's entry goes when it is filled
  const [fieldErrors, setFieldErrors] = useState({});
  // RCMC-18: the field to focus after a failed Add (a new object each time, so a repeat Add refocuses)
  const [focusRequest, setFocusRequest] = useState(null);

  // Focus after the render that shows the errors, so the field is read with "invalid" and its error text
  useEffect(() => {
    if (focusRequest) document.getElementById(focusRequest.id)?.focus();
  }, [focusRequest]);

  /** Store a field's value; once it has one, its error goes (the next Add checks every field again) */
  const setField = (key, value) => {
    setSelectedNewValues((prevState) => ({...prevState, [key]: value}));
    if (!isBlank(value)) setFieldErrors((prevErrors) => ({...prevErrors, [key]: undefined}));
  };

  /** Required state + error wiring for the PrimeReact text fields (the shared Select does its own) */
  const textFieldA11y = (key) => {
    const error = fieldErrors[key];
    return {
      "aria-required": "true",
      "aria-invalid": error ? "true" : undefined,
      "aria-describedby": error ? `${FIELD[key].id}-error` : undefined,
      className: error ? "p-invalid" : undefined,
    };
  };

  /** A text field's error line, linked from the field by aria-describedby */
  const fieldError = (key) =>
    fieldErrors[key] && <div id={`${FIELD[key].id}-error`} className={styles["field-error"]}>{fieldErrors[key]}</div>;

  /** RCMC-01: forget the chosen Supporting Document (input, form state, its "chosen" message); `error` goes on its control */
  const dropSupportingDocument = (error) => {
    if (inputRef.current) inputRef.current.value = ""; // so choosing the same file again still fires a change
    messages.clearStatus();
    setSelectedNewValues((prevState) => ({...prevState, supporting_document: ""}));
    setFieldErrors((prevErrors) => ({...prevErrors, supporting_document: error}));
  };

  // RCMC-01 (ticket 07): a picked file is checked for size and type at once (no browser alert() any more). A
  // refused file is not kept: its error goes on the upload control and to the alert region (ruling Q29), and
  // focus stays on the control, where the picker left it. An accepted file is announced (ruling Q30).
  const chooseSupportingDocument = (file) => {
    if (!file) return; // picker cancelled: keep what was chosen before (as v1.2)
    const error = supportingDocumentError(file);
    if (error) {
      dropSupportingDocument(error);
      messages.alert(error, "file");
      return;
    }
    // The file's own refusal is out of date; anything else in the alert region (the required-fields summary) still holds
    messages.clearAlert("file");
    setSelectedNewValues((prevState) => ({...prevState, supporting_document: file}));
    setFieldErrors((prevErrors) => ({...prevErrors, supporting_document: undefined}));
    messages.status(`Supporting document chosen — ${file.name}.`);
  };

  // RCMC-01: Remove clears the file and puts focus back on the upload control (the button itself goes)
  const removeSupportingDocument = () => {
    const { name } = selectedNewValues.supporting_document;
    dropSupportingDocument(undefined);
    inputRef.current.focus();
    messages.status(`Supporting document removed — ${name}.`);
  };

  const exportCSV = (selectionOnly) => {
    dt.current.exportCSV({ selectionOnly });
  };

  const rowCounts = [
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "100", value: 100 },
  ];

  const openModalHandler = () => {
    dialogSession.renew();
    setIsAddNewModalVisible(true);
  }

  const closeModalHandler = () => {
    dialogSession.renew(); // a reply still on its way belongs to a closed dialog now (ticket 22)
    setIsAddNewModalVisible(false);
    setAddingNew(false); // the next opening's Add is ready, whatever an earlier one still awaits
    setSelectedNewValues(EMPTY_REQUEST);
    setFieldErrors({});
    setFocusRequest(null);
    // RCMC-18: a "required fields are empty" summary (or an earlier failure) leaves with the form
    messages.clearAlert();
    // RCMC-01: so does a "Supporting document chosen / removed" message. Only the form's own (ticket 22): another
    // screen's message, e.g. the page's companies failing to load, stays.
    messages.clearStatus();
    // v1.2 cleared the input here on every reply, and threw once the dialog had gone (a request that had been raised
    // was announced as "Something went wrong"); a reply no longer comes here after a close
    if (inputRef.current) inputRef.current.value = "";
  }

  const addNewServiceRequest = (event) => {
    // RCMC-18: with a mandatory field empty nothing is sent. Each empty field shows its error, the alert
    // region lists them, and focus goes to the first.
    const missing = REQUIRED_FIELDS.filter((field) => isBlank(selectedNewValues[field.key]));
    if (missing.length > 0) {
      // The upload's own error stays: its file is still refused (RCMC-01, ruling Q29)
      setFieldErrors((prevErrors) => ({
        supporting_document: prevErrors.supporting_document,
        ...Object.fromEntries(missing.map((field) => [field.key, field.error])),
      }));
      messages.alert(missingSummary(missing), "form");
      setFocusRequest({ id: missing[0].id });
      return;
    }
    // Every field is filled, so an earlier summary is out of date; this attempt's outcome is announced below
    messages.clearAlert();
    setAddingNew(true);
    const isCurrent = dialogSession.capture();
    const formData = new FormData();
    formData.append("company_name",selectedNewValues.company_name);
    formData.append("folio_no",selectedNewValues.folio_no);
    formData.append("certificate_no",selectedNewValues.certificate_no);
    formData.append("request_type",selectedNewValues.request_type);
    formData.append("description",selectedNewValues.description);
    formData.append("supporting_document",selectedNewValues.supporting_document);
    axios
    .post("/api/user/service-request-details", formData , {
      headers: {"Content-Type": "multipart/form-data", Authorization: "Bearer " + authCtx.authTokens.access },
    })
    .then((res) => {
      const created = res.data.service_request_details;
      serviceCtx.setListOfServiceRequests(prevState => [...prevState,created]);
      // The dialog that sent it closes; one closed (or opened again) since is left as it is (ticket 22)
      if (isCurrent()) closeModalHandler();
      // RCMC-17: announce the outcome, with the new Request ID and Stage from the create reply. Not the form's message:
      // it outlives the dialog
      globalCtx.raiseAlert(`Service request ${created.request_id} raised — ${created.status_display}.`, "success");
    })
    .catch((e) => {
      const message = errorMessage(e);
      if (!isCurrent()) {
        // Closed (or opened again) mid-request: the alert is all that is left to say (ticket 22: v1.3 before it marked
        // a dialog opened since with the old file's refusal)
        globalCtx.raiseAlert(message, "error");
        return;
      }
      setAddingNew(false); // focus is still on Add (ticket 22), ready for another try
      // RCMC-01 (ticket 07): the server refused the Supporting Document (size, type or content). Like a refusal
      // in the browser: the message goes on the upload control, the file is dropped, and focus goes there.
      if (e.response?.data?.field === "supporting_document") {
        dropSupportingDocument(message);
        messages.alert(message, "file");
        setFocusRequest({ id: UPLOAD_ID });
      } else {
        messages.alert(message, "form");
      }
    });
  }
  
  // RCMC-10 (ticket 13): a real link to the request's page (v1.2: a div with a click handler, so neither the keyboard
  // nor a screen reader could open it). Relative to this list's route, so it serves both portals. v1.2 also stored the
  // row in the context's selectedRequest, which nothing reads (the details page loads the request itself).
  const requestIdBodyTemplate = (request) => {
    return <Link className={styles['request_id']} to={request.request_id}>{request.request_id}</Link>;
  };

  // A column's filter popup: pick any of the column's values. RCMC-11 (ticket 13): its search box and list are named
  // for the column, and the list tells a screen reader which value the arrow keys are on (Util/dataTable.jsx).
  // (v1.2 had one copy of this per column, each logging the choice to the console.)
  const listFilterTemplate = (column, values) => (options) => <ColumnFilterList column={column} listStyle={{ maxHeight: '30vh' }} multiple filter value={options.value} onChange={(e) => options.filterApplyCallback(e.value)} options={values} className="w-full md:w-14rem" />;

  // RCMC-04 (ticket 16): `table-page` / `table-card` let the page flow below 768px (App.css)
  return (
    <div className="table-page" style={{ padding: "10px", maxHeight: "100vh", overflow: "hidden" }}>
      {/* RCMC-04 (ticket 16): the page name and New Request wrap onto two lines when the window is too narrow for one */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "space-between", alignItems:"center",paddingBottom:"10px" }}>
        {/* RCMC-08 (ticket 11): the page name is the page's one h1, same look */}
        <PageHeading icon={<MdMiscellaneousServices style={{marginRight:"5px"}} />}>Service Requests</PageHeading>
        {!authCtx.user.is_admin && <button type="button"
          onClick={openModalHandler}
          style={{ background: "var(--portal-primary)", border:"none",boxShadow:"rgba(0, 0, 0, 0.24) 0px 3px 8px",borderRadius:"5px",color:"var(--portal-on-primary)", padding: "8px 20px",display:'flex',alignItems:"center"}}
        ><IoAdd size={15} />New Request</button>}
        <Dialog
          header="Raise service request"
          visible={isAddNewModalVisible}
          style={{ width: "50vw" }}
          onHide={closeModalHandler}
          {...raiseDialogIsolation}
        >
            {/* RCMC-18: the key to the "*" on each mandatory field's label (the "*" itself is hidden from
                screen readers; each field is announced as required instead). Labels come from FIELD, so the
                alert summary names fields exactly as they read on screen. In the PrimeReact float-label
                fields the "*" sits inside the <label> (FloatLabel positions the label, so a sibling would
                not follow it); aria-hidden keeps it out of the name. The shared Select puts it after the label. */}
            <p className={styles["required-key"]}>* Required</p>
            {/* RCMC-02: native selects (shared Select, owner ruling Q12), announced with their labels and the
                chosen name. Option values stay the company id and the Request Type key, as v1.2 sent them. */}
            <Select
              id={FIELD.company_name.id}
              label={FIELD.company_name.label}
              placeholder="Select a company"
              options={serviceCtx.listOfCompanies}
              value={selectedNewValues.company_name}
              onChange={(companyId) => setField("company_name", companyId)}
              required
              error={fieldErrors.company_name}
              className={styles["dialog-field"]}
            />
            <div className={styles["dialog-field"]}>
              <FloatLabel style={{width:"100%"}}>
                  <InputText id={FIELD.folio_no.id} value={selectedNewValues.folio_no} onChange={(e) => setField("folio_no", e.target.value)} {...textFieldA11y("folio_no")} />
                  <label htmlFor={FIELD.folio_no.id}>{FIELD.folio_no.label}<span className={styles["required-mark"]} aria-hidden="true">*</span></label>
              </FloatLabel>
              {fieldError("folio_no")}
            </div>
            <div className={styles["dialog-field"]}>
              <FloatLabel style={{width:"100%"}}>
                  <InputText id={FIELD.certificate_no.id} value={selectedNewValues.certificate_no} onChange={(e) => setField("certificate_no", e.target.value)} {...textFieldA11y("certificate_no")} />
                  <label htmlFor={FIELD.certificate_no.id}>{FIELD.certificate_no.label}<span className={styles["required-mark"]} aria-hidden="true">*</span></label>
              </FloatLabel>
              {fieldError("certificate_no")}
            </div>
            {/* v1.2's label read "Request Types" */}
            <Select
              id={FIELD.request_type.id}
              label={FIELD.request_type.label}
              placeholder="Select a request type"
              options={serviceCtx.listOfRequestTypes}
              value={selectedNewValues.request_type}
              onChange={(requestType) => setField("request_type", requestType)}
              required
              error={fieldErrors.request_type}
              className={styles["dialog-field"]}
            />
            {/* No bottom margin, as in v1.2: the upload row sits directly below */}
            <div>
              <span className="p-float-label">
                  <InputTextarea id={FIELD.description.id} value={selectedNewValues.description} onChange={(e) => setField("description", e.target.value)} rows={5} cols={30} {...textFieldA11y("description")} />
                  <label htmlFor={FIELD.description.id}>{FIELD.description.label}<span className={styles["required-mark"]} aria-hidden="true">*</span></label>
              </span>
              {fieldError("description")}
            </div>
            {/* RCMC-01: optional (ruling Q14), so not in REQUIRED_FIELDS; its error uses the same wiring */}
            <SupportingDocumentUpload
              inputRef={inputRef}
              file={selectedNewValues.supporting_document}
              error={fieldErrors.supporting_document}
              onChoose={chooseSupportingDocument}
              onRemove={removeSupportingDocument}
            />
            {/* Ticket 22 (Q35): busy without being disabled, so focus stays on it (Util/busyButton.js) */}
            <Button label="Add" raised style={{width:"100%"}} {...busyButtonProps(isAddingNew, addNewServiceRequest)} />
        </Dialog>
      </div>
      <div
        className="table-card"
        style={{
          background: "white",
          borderRadius: "10px",
          maxHeight: "90%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          fontSize: "14px",
          boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
        }}
      >
        <div
          style={{
            display: "flex",
            paddingBlock: "5px",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex",paddingLeft:"10px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              {/* RCMC-10 (ticket 13): the shared native select, labelled (v1.2's PrimeReact Dropdown was unnamed) */}
              <Select
                id="service-requests-rows-per-page"
                label="Rows per page"
                inline
                options={rowCounts}
                value={selectedRowCount}
                onChange={setSelectedRowCount}
              />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: "10px",
            }}
          >
            {/* RCMC-02 (ticket 13): named for what it exports; v1.2's data-pr-tooltip did nothing (no <Tooltip>) */}
            <Button
              type="button"
              rounded
              onClick={() => exportCSV(false)}
              aria-label="Export service requests as CSV"
            ><IoMdDownload aria-hidden="true" /></Button>
          </div>
        </div>
        <DataTable
          ref={dt}
          value={serviceCtx.listOfServiceRequests}
          paginator
          rows={selectedRowCount}
          filters={filters}
          stripedRows
          size="small"
          dataKey="id" globalFilterFields={['created_on','request_id','status_display']} 
          tableStyle={{ fontSize: "14px" }} // RCMC-04 (ticket 16): no fixed 50rem minimum; too wide, it scrolls in its region
          pt={tablePassThrough()} // RCMC-10 (ticket 13): named by the h1, valid row groups, scoped headers, no clickable rows; RCMC-04 (16): its region
        >
          <Column field="created_on" {...columnProps("Date", { sortable: true, filter: true })} showFilterMatchModes={false} showApplyButton={false} filterField="created_on" filterElement={listFilterTemplate("Date", serviceCtx.listOfDistictValues.created_on_list)} body={(request) => <div style={{whiteSpace:"nowrap"}}>{request.created_on}</div>}></Column>
          <Column field="request_id" {...columnProps("Request ID", { sortable: true, filter: true })} showFilterMatchModes={false} showApplyButton={false} filterField="request_id" filterElement={listFilterTemplate("Request ID", serviceCtx.listOfDistictValues.request_id_list)} body={requestIdBodyTemplate}></Column>
          <Column field="company_name" {...columnProps("Company", { sortable: true })}></Column>
          <Column field="folio_no" {...columnProps("Folio No / DP-Client ID", { sortable: true })}></Column>
          <Column field="request_type" {...columnProps("Request Type", { sortable: true })}></Column>
          <Column field="status_display" {...columnProps("Status", { sortable: true, filter: true })} showFilterMatchModes={false} showApplyButton={false} filterField="status_display" filterElement={listFilterTemplate("Status", serviceCtx.listOfDistictValues.stages_list)}></Column>
        </DataTable>
      </div>
    </div>
  );
};

export default ServiceRequest;
